import { Injectable } from '@angular/core';
import { UserRoles } from '@authentication/user-roles';
import {
  DriveOrderActivationReminderToSubcontractor,
  DriveOrderAssigned,
  DriveOrderInquiryCreated,
  DriveOrderInquiryReply,
  DriverMessageOnTripStopUpdated,
  NotificationMessage,
  NotificationType,
  OrderCancelled,
  OrderCreated,
  OrderEdited,
  PossibleLateFirstDeparture,
  DriveOrderTripsCancelled,
  DriveOrderSubcontractorChanged,
  DriveOrderDuplicated,
  DriveOrderTripsDuplicated,
  DriveOrderTripsReplaced,
  UserMessageOnIntegrationErrorDriveOrderCreationFailed,
  UserMessageOnIntegrationErrorInvalidStartPlaceForSubcontractor,
  UserMessageOnIntegrationErrorInvalidVehicleType,
  UserMessageOnIntegrationErrorUnsupportedVehicleSize,
  UserMessageOnIntegrationErrorNotFoundAddresses,
  DriveOrderCancelledRequiresManualHandling,
} from '@models/notification-message';
import moment from 'moment';
import { AuthService } from './auth.service';
import { VehicleTypeService } from './vehicle-type.service';

@Injectable()
export class ServerNotificationService {
  UserRoles = UserRoles;
  userIsNobinaLeveransansvarig = false;
  constructor(private authService: AuthService, private vehicleTypeService: VehicleTypeService) {}

  init() {
    this.userIsNobinaLeveransansvarig = this.authService.userHasAnyRole([
      UserRoles.NobinaLeveransansvarig,
    ]);
  }

  setServerNotificationLocalization(notification: NotificationMessage): NotificationMessage {
    switch (notification.notificationType) {
      case NotificationType.DriveOrderInquiryCreated:
        this.setDriveOrderInquiryCreatedNotification(notification);
        break;
      case NotificationType.DriveOrderInquiryReply:
        this.setDriveOrderInquiryReplyNotification(notification);
        break;
      case NotificationType.DriveOrderAssigned:
        this.setDriveOrderAssignedNotification(notification);
        break;
      case NotificationType.DriveOrderTripsCancelled:
        this.setDriveOrderTripsCancelledNotification(notification);
        break;
      case NotificationType.DriveOrderDuplicated:
        this.setDriveOrderDuplicatedNotification(notification);
        break;
      case NotificationType.DriveOrderTripsDuplicated:
        this.setDriveOrderTripsDuplicatedNotification(notification);
        break;
      case NotificationType.DriveOrderTripsReplaced:
        this.setDriveOrderTripsReplacedNotification(notification);
        break;
      case NotificationType.DriveOrderSubcontractorChanged:
        this.setDriveOrderSubcontractorChangedNotification(notification);
        break;
      case NotificationType.PossibleLateFirstDeparture:
        this.setPossibleLateFirstDepartureNotification(notification);
        break;
      case NotificationType.OrderCreated:
        this.setOrderCreatedNotification(notification);
        break;
      case NotificationType.OrderEdited:
        this.setOrderEditedNotification(notification);
        break;
      case NotificationType.OrderCancelled:
        this.setOrderCancelledNotification(notification);
        break;
      case NotificationType.DriveOrderActivationReminderToSubcontractor:
        this.setDriveOrderActivationReminderToSubcontractorNotification(notification);
        break;
      case NotificationType.DriverMessageOnTripStopUpdated:
        this.setDriverMessageOnTripStopUpdatedNotification(notification);
        break;
      case NotificationType.DriveOrderCancelledRequiresManualHandling:
        this.setDriveOrderCancelledRequiresManualHandlingNotification(notification);
        break;
      case NotificationType.IntegrationErrorDriveOrderCreationFailed:
        this.setIntegrationErrorDriveOrderCreationFailedNotification(notification);
        break;
      case NotificationType.IntegrationErrorInvalidStartPlaceForSubcontractor:
        this.setIntegrationErrorInvalidStartPlaceForSubcontractorNotification(notification);
        break;
      case NotificationType.IntegrationErrorNotFoundAddresses:
        this.setIntegrationErrorNotFoundAddressesNotification(notification);
        break;
      case NotificationType.IntegrationErrorInvalidVehicleType:
        this.setIntegrationErrorInvalidVehicleTypeNotification(notification);
        break;
      case NotificationType.IntegrationErrorUnsupportedVehicleSize:
        this.setIntegrationErrorUnsupportedVehicleSizeNotification(notification);
        break;
      default:
        break;
    }
    return notification;
  }

  private setDriveOrderAssignedNotification(notification: NotificationMessage) {
    const driveOrderAssignedData = notification.data as unknown as DriveOrderAssigned;
    const numberOfDriveOrders = driveOrderAssignedData.numberOfDriveOrders;
    notification.title = $localize`:@@drive-order.assigned-notification.title:Ny tilldelning`;

    if (numberOfDriveOrders > 1) {
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-orders.assigned-notification.message:Du har blivit tilldelad ${numberOfDriveOrders} nya körordrar`,
        numberOfDriveOrders.toString()
      );
    } else {
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.assigned-notification.message:Du har blivit tilldelad ${numberOfDriveOrders} ny körorder`,
        numberOfDriveOrders.toString()
      );
    }
    notification.link = 'orders';
  }

  private setDriveOrderSubcontractorChangedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderSubcontractorChanged;
    const { orderId, driveOrderId, subcontractorName } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@drive-order.subcontractor-changed-notification.title:Körorder tilldelad: ${driveOrderId}`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@drive-order.subcontractor-changed-notification.message:Körorder ${driveOrderId} tilldelad till ${subcontractorName}.`,
      driveOrderId.toString(),
      subcontractorName
    );

    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  private setDriveOrderCancelledRequiresManualHandlingNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderCancelledRequiresManualHandling;
    const { orderId, driveOrderId } = data;
    notification.title = $localize`:@@drive-order.cancelled-requires-manual-handling-notification.title:Körorder avbeställd - manuell hantering krävs`;
    notification.main = this.translationStringSubstitution(
      $localize`:@@drive-order.cancelled-requires-manual-handling-notification.message:Körorder ${driveOrderId} har avbeställts och kräver manuell hantering.`,
      driveOrderId.toString()
    );

    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  private setDriveOrderInquiryCreatedNotification(notification: NotificationMessage) {
    const driveOrderInquiryCreatedData = notification.data as unknown as DriveOrderInquiryCreated;
    const numberOfInquiries = driveOrderInquiryCreatedData.numberOfInquiries;
    notification.title = $localize`:@@drive-order.inquiry-notification.title:Ny inkommen förfrågan`;

    if (numberOfInquiries > 1) {
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.inquiries-notification.message:Du har ${numberOfInquiries} nya förfrågningar`,
        numberOfInquiries.toString()
      );
    } else {
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.inquiry-notification.message:Du har ${numberOfInquiries} ny förfrågan`,
        numberOfInquiries.toString()
      );
    }
    notification.link = `${window.location.origin}/order-new/planning`;
  }

  private setDriveOrderInquiryReplyNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderInquiryReply;

    notification.title = this.translationStringSubstitution(
      $localize`:@@drive-order.inquiry-reply-notification.title:UE har besvarat förfrågan: ${data.driveOrderId}`,
      data.driveOrderId.toString()
    );

    const args = [
      data.subcontractorName,
      data.startPlaceDescription,
      data.endPlaceDescription,
    ] as const;

    switch (data.inquiryStatus) {
      case 'Accepted':
        notification.main = this.translationStringSubstitution(
          $localize`:@@drive-order.inquiry-reply-notification.accepted-message:${data.subcontractorName} svarade JA på förfrågan: ${data.startPlaceDescription} - ${data.endPlaceDescription}.`,
          ...args
        );
        break;
      case 'Tentative':
        notification.main = this.translationStringSubstitution(
          $localize`:@@drive-order.inquiry-reply-notification.tentative-message:${data.subcontractorName} svarade KANSKE på förfrågan: ${data.startPlaceDescription} - ${data.endPlaceDescription}.`,
          ...args
        );
        break;
      case 'Rejected':
        notification.main = this.translationStringSubstitution(
          $localize`:@@drive-order.inquiry-reply-notification.rejected-message:${data.subcontractorName} svarade NEJ på förfrågan: ${data.startPlaceDescription} - ${data.endPlaceDescription}.`,
          ...args
        );
        break;
      default:
        return '';
    }

    notification.link = data.isProductBased
      ? `/order-new/order/${data.orderId}/assignment`
      : '/order-new/planning';
  }

  private setDriveOrderTripsCancelledNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderTripsCancelled;
    const { orderId, driveOrderId, isSingleTrip, tripId, startStopPasstime, startStopDescription } = data;
    const startTime = moment(startStopPasstime).format('HH:mm');

    if (isSingleTrip) {
      notification.title = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-cancelled-notification.single-trip.title:Tur inställd`
      );
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-cancelled-notification.single-trip.message:Tur ${tripId.toString()} med avgångstid ${startTime} från ${startStopDescription} är inställd.`
      );
      notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${driveOrderId}_${tripId}`;
    } else {
      notification.title = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-cancelled-notification.several-trips.title:Turer inställda`
      );
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-cancelled-notification.several-trips.message:Körorder ${driveOrderId.toString()} har inställda turer.`
      );
      notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${driveOrderId}`;
    }
  }

  private setDriveOrderDuplicatedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderDuplicated;
    const { orderId, driveOrderId, newDriveOrderId } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@drive-order.duplicated-notification.title:Körorder förstärkt`
    );

    notification.main = this.translationStringSubstitution(
      $localize`:@@drive-order.duplicated-notification.message:Körorder ${driveOrderId} är förstärkt med körorder ${newDriveOrderId}`,
      driveOrderId.toString(),
      newDriveOrderId.toString()
    );

    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${newDriveOrderId}`;
  }

  private setDriveOrderTripsDuplicatedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderTripsDuplicated;
    const { orderId, driveOrderId, newDriveOrderId, isSingleTrip } = data;

    if (isSingleTrip) {
      notification.title = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-duplicated-notification.single.title:Tur förstärkt`
      );
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-duplicated-notification.single.message:Tur på körorder ${driveOrderId} är förstärkt med körorder ${newDriveOrderId}.`,
        driveOrderId.toString(),
        newDriveOrderId.toString()
      );
    } else {
      notification.title = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-duplicated-notification.multiple.title:Turer förstärkta`
      );
      notification.main = this.translationStringSubstitution(
        $localize`:@@drive-order.trips-duplicated-notification.multiple.message:Turer på körorder ${driveOrderId} är förstärkta med körorder ${newDriveOrderId}.`,
        driveOrderId.toString(),
        newDriveOrderId.toString()
      );
    }

    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${newDriveOrderId}`;
  }

  private setDriveOrderTripsReplacedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as DriveOrderTripsReplaced;
    const { orderId, driveOrderId, newDriveOrderId } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@drive-order.trips-replaced-notification.title:Körorder delad`
    );

    notification.main = this.translationStringSubstitution(
      $localize`:@@drive-order.trips-replaced-notification.message:Körorder ${driveOrderId} har delats till körorder ${newDriveOrderId}`,
      driveOrderId.toString(),
      newDriveOrderId.toString()
    );

    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${newDriveOrderId}`;
  }

  private setPossibleLateFirstDepartureNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as PossibleLateFirstDeparture;
    const { orderId, driveOrderId, tripId, startTripStopTime } = data;
    const time = moment(startTripStopTime).format('HH:mm');

    notification.title = this.translationStringSubstitution(
      $localize`:@@drive-order.possible-late-first-departure-notification.title:Framkörning ej inledd för körorder ${driveOrderId}`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@drive-order.possible-late-first-departure-notification.message:Körorder ${driveOrderId} med tur ${tripId}, starttid klockan ${time} har ej inlett framkörning.`,
      driveOrderId.toString(),
      tripId.toString(),
      time
    );
    notification.link = `${window.location.origin}/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  private setOrderCreatedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as OrderCreated;
    const {
      orderId,
      operatorName,
      orderType,
      isProductBased,
      routeName,
      routeStartPlaceDescription,
      routeEndPlaceDescription,
    } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@order.order-created-notification.title:Ny beställning, ${orderId}`,
      orderId.toString()
    );
    notification.main = isProductBased
      ? this.translationStringSubstitution(
          $localize`:@@order.order-created-notification.product-based.message:${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        )
      : this.translationStringSubstitution(
          $localize`:@@order.order-created-notification.message:${routeName} ${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeName,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        );
    notification.submain = this.translationStringSubstitution(
      $localize`:@@order.order-created-notification.sub-message:${operatorName}`
    );
    notification.type = orderType;
    notification.link = isProductBased ? `/order/product-order/${orderId}` : `/order/${orderId}`;
  }

  private setOrderEditedNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as OrderEdited;
    const {
      orderId,
      operatorName,
      orderType,
      isProductBased,
      routeName,
      routeStartPlaceDescription,
      routeEndPlaceDescription,
    } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@order.order-edited-notification.title:Ändrad beställning, ${orderId}`,
      orderId.toString()
    );
    notification.main = isProductBased
      ? this.translationStringSubstitution(
          $localize`:@@order.order-edited-notification.product-based.message:${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        )
      : this.translationStringSubstitution(
          $localize`:@@order.order-edited-notification.message:${routeName} ${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeName,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        );

    if (this.userIsNobinaLeveransansvarig) {
      notification.submain = this.translationStringSubstitution(
        $localize`:@@order.order-edited-notification.sub-message:${operatorName}`
      );
    }

    notification.type = orderType;
    notification.link = isProductBased ? `/order/product-order/${orderId}` : `/order/${orderId}`;
  }

  private setOrderCancelledNotification(notification: NotificationMessage) {
    const data = notification.data as unknown as OrderCancelled;
    const {
      orderId,
      operatorName,
      isProductBased,
      routeName,
      routeStartPlaceDescription,
      routeEndPlaceDescription,
    } = data;

    notification.title = this.translationStringSubstitution(
      $localize`:@@order.order-cancelled-notification.title:Avbeställning, ${orderId}`,
      orderId.toString()
    );
    notification.main = isProductBased
      ? this.translationStringSubstitution(
          $localize`:@@order.order-cancelled-notification.product-based.message:${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        )
      : this.translationStringSubstitution(
          $localize`:@@order.order-cancelled-notification.message:${routeName} ${routeStartPlaceDescription} - ${routeEndPlaceDescription}`,
          routeName,
          routeStartPlaceDescription,
          routeEndPlaceDescription
        );
    notification.submain = this.translationStringSubstitution(
      $localize`:@@order.order-cancelled-notification.sub-message:${operatorName}`
    );
    notification.link = isProductBased ? `/order/product-order/${orderId}` : `/order/${orderId}`;
  }

  private setDriveOrderActivationReminderToSubcontractorNotification(
    notification: NotificationMessage
  ) {
    const data = notification.data as unknown as DriveOrderActivationReminderToSubcontractor;
    const { subcontractorId, subcontractorOrderId, driveOrderId, driverFullName, startTime } = data;

    const time = moment(startTime).format('HH:mm');
    notification.title = this.translationStringSubstitution(
      $localize`:@@order.order-activated-notification.title:Körorderaktivering påminnelse, ${driveOrderId}`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@order.order-activated-notification.message:Förare ${driverFullName} har ej aktiverat körorder ${driveOrderId} med starttid ${time}`,
      driverFullName,
      driveOrderId.toString(),
      time
    );
    notification.link = `${window.location.origin}/order-new/subcontractor/${subcontractorId}/subcontractor-orders/${subcontractorOrderId}/drive-order#${driveOrderId}`;
  }

  setDriverMessageOnTripStopUpdatedNotification(notification: NotificationMessage) {
    const driverMessageOnTripStopUpdatedData =
      notification.data as unknown as DriverMessageOnTripStopUpdated;
    const passTime = new Date(driverMessageOnTripStopUpdatedData.passTime);
    const tripId = driverMessageOnTripStopUpdatedData.tripId.toString();
    const driveOrderId = driverMessageOnTripStopUpdatedData.driveOrderId;
    const orderId = driverMessageOnTripStopUpdatedData.orderId;
    const operatorName = driverMessageOnTripStopUpdatedData.operatorName;
    const placeDescription = driverMessageOnTripStopUpdatedData.placeDescription;
    const time = passTime
      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      .toString();
    const message = driverMessageOnTripStopUpdatedData.message;
    const driverFullName = driverMessageOnTripStopUpdatedData.driverFullName;
    const driverPhoneNumber = driverMessageOnTripStopUpdatedData.driverPhoneNumber;

    notification.title = this.translationStringSubstitution(
      $localize`:@@stop.driver-message-on-trip-stop-updated-notification:${operatorName} hållplatsmeddelande till förare`,
      operatorName
    );

    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}_${tripId}`;

    notification.main = this.translationStringSubstitution(
      $localize`:@@stop.driver-message-on-trip-stop-updated-notification-message:Tur ${tripId}, hållplats ${placeDescription}, tid ${time}, meddelande: ${message}. Förare: ${driverFullName}, ${driverPhoneNumber}`,
      tripId,
      placeDescription,
      time,
      message,
      driverFullName,
      driverPhoneNumber
    );

    const infoList: { label: string; value: string }[] = [];
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-driver-message:Förarmeddelande`,
      value: driverMessageOnTripStopUpdatedData.message,
    });
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-trip:Tur`,
      value: tripId,
    });
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-stop:Hållplats`,
      value: driverMessageOnTripStopUpdatedData.placeDescription,
    });
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-time:Tid`,
      value: passTime
        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        .toString(),
    });
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-driver:Förare`,
      value: driverMessageOnTripStopUpdatedData.driverFullName,
    });
    infoList.push({
      label: $localize`:@@stop.driver-message-on-trip-stop-updated-notification-telephone-number:Telefonnummer`,
      value: driverMessageOnTripStopUpdatedData.driverPhoneNumber,
    });
    notification.infoList = infoList;
  }

  setIntegrationErrorDriveOrderCreationFailedNotification(notification: NotificationMessage) {
    const messageData =
      notification.data as unknown as UserMessageOnIntegrationErrorDriveOrderCreationFailed;
    const orderId = messageData.orderId;
    const driveOrderId = messageData.driveOrderId;
    const errorMessage = messageData.errorMessage;

    notification.title = this.translationStringSubstitution(
      $localize`:@@integration-error.drive-order-creation-failed-notification.title:Allmänt fel (körorder ${driveOrderId})`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@integration-error.drive-order-creation-failed-notification.message:${errorMessage}`,
      errorMessage
    );
    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  setIntegrationErrorInvalidStartPlaceForSubcontractorNotification(notification: NotificationMessage) {
    const messageData =
      notification.data as unknown as UserMessageOnIntegrationErrorInvalidStartPlaceForSubcontractor;
    const orderId = messageData.orderId;
    const driveOrderId = messageData.driveOrderId;
    const subcontractorName = messageData.subcontractorName;
    const placeName  = messageData.placeName;

    notification.title = this.translationStringSubstitution(
      $localize`:@@integration-error.invalid-start-place-for-subcontractor-notification.title:Ogiltig startplats för vald utförare (körorder ${driveOrderId})`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@integration-error.invalid-start-place-for-subcontractor-notification.message:Utförare ${subcontractorName} kan inte hantera startplats ${placeName}.`,
      subcontractorName,
      placeName
    );
    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  setIntegrationErrorNotFoundAddressesNotification(notification: NotificationMessage) {
    const messageData =
      notification.data as unknown as UserMessageOnIntegrationErrorNotFoundAddresses;
    const orderId = messageData.orderId;
    const driveOrderId = messageData.driveOrderId;
    const subcontractorName = messageData.subcontractorName;

    notification.title = this.translationStringSubstitution(
      $localize`:@@integration-error.not-found-addresses-notification.title:Tekniskt fel vid uppslag av adresser (körorder ${driveOrderId})`,
      driveOrderId.toString() 
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@integration-error.not-found-addresses-notification.message:Den här körordern kan inte hanteras av ${subcontractorName}.`,
      subcontractorName
    );
    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  setIntegrationErrorInvalidVehicleTypeNotification(notification: NotificationMessage) {
    const messageData = notification.data as unknown as UserMessageOnIntegrationErrorInvalidVehicleType;
    const orderId = messageData.orderId;
    const driveOrderId = messageData.driveOrderId;
    const subcontractorName = messageData.subcontractorName;
    const vehicleType = this.vehicleTypeService.getVehicleTypeByString(messageData.vehicleType.toString());

    notification.title = this.translationStringSubstitution(
      $localize`:@@integration-error.invalid-vehicle-type-notification.title:Felaktig fordonstyp (körorder ${driveOrderId})`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@integration-error.invalid-vehicle-type-notification.message:Utförare ${subcontractorName} kan inte hantera fordonstyp ${vehicleType}.`,
      subcontractorName,
      vehicleType
    );
    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  setIntegrationErrorUnsupportedVehicleSizeNotification(notification: NotificationMessage) {
    const messageData = notification.data as unknown as UserMessageOnIntegrationErrorUnsupportedVehicleSize;
    const orderId = messageData.orderId;
    const driveOrderId = messageData.driveOrderId;
    const subcontractorName = messageData.subcontractorName;
    const vehicleType = this.vehicleTypeService.getVehicleTypeByString(messageData.vehicleType.toString());
    const supportedMaxSize = messageData.supportedMaxSize;
    
    notification.title = this.translationStringSubstitution(
      $localize`:@@integration-error.unsupported-vehicle-size-notification.title:Ogiltig fordonstyp (körorder ${driveOrderId})`,
      driveOrderId.toString()
    );
    notification.main = this.translationStringSubstitution(
      $localize`:@@integration-error.unsupported-vehicle-size-notification.message:Fordonstyp ${vehicleType} kan inte hanteras. Utförare ${subcontractorName} kan hantera max ${supportedMaxSize} passagerare per körorder.`,
      vehicleType,
      subcontractorName,
      supportedMaxSize.toString()
    );
    notification.link = `/order-new/order/${orderId}/drive-order#${driveOrderId}`;
  }

  private translationStringSubstitution(translation: string, ...args: string[]): string {
    args.forEach((arg, index) => {
      translation = translation.replace(`{${index}}`, arg);
    });
    return translation;
  }

  userHasAnyRole(userRoles: UserRoles[]): boolean {
    return this.authService.userHasAnyRole(userRoles);
  }
}
